/**
 * Pure helpers for the desktop-web Sources / Outputs panel.
 * Kept free of React so unit tests can exercise classification without
 * mounting the panel.
 */

import { presentWorkArtifacts } from '@/components/Coworker/coworkerArtifactTypes';

/** How many Outputs stay in the rail before “View all” opens the modal. */
export const OUTPUTS_RAIL_PREVIEW_LIMIT = 5;

/**
 * Visible Outputs-rail label. Keep the extension so a long
 * `Gab_AI___Investor_Executive_Summary.docx` still reads as a Word file.
 */
export function shortenFileNameKeepExt(name, max = 20) {
  const raw = String(name || '').trim();
  if (!raw || raw.length <= max) return raw;
  const dot = raw.lastIndexOf('.');
  const ext = dot > 0 && dot >= raw.length - 8 ? raw.slice(dot) : '';
  const base = ext ? raw.slice(0, dot) : raw;
  const keep = Math.max(8, max - ext.length - 1);
  if (base.length <= keep) return raw;
  return `${base.slice(0, keep)}…${ext}`;
}

function getBaseHostname(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (_) {
    return url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .split(':')[0];
  }
}

function isPlatformCdnHost(host) {
  return host === 'cdn.gab.ai' || String(host || '').endsWith('.cdn.gab.ai');
}

function isInferenceFile(file) {
  const source = file?.source;
  return source === 'inference' || source === 'ai_generated' || source === 'ai-generated';
}

export function getFileDisplayName(file) {
  const raw = file?.name || file?.originalName || file?.parameters?.title || '';
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (trimmed) return trimmed;
  const type = file?.fileType || file?.type;
  if (type === 'image') return 'Generated image';
  if (type === 'video') return 'Generated video';
  if (type === 'audio') return 'Generated audio';
  if (type === 'document') return 'Document';
  if (type === 'spreadsheet') return 'Spreadsheet';
  return 'File';
}

export function outputFilePublicUrl(file) {
  const url = file?.cdnPath || file?.url || file?.uri;
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  return trimmed || null;
}

export function outputWorkArtifactPath(file) {
  const path = file?.path || file?.parameters?.workArtifactPath;
  return typeof path === 'string' && path.trim() ? path.trim() : null;
}

export function outputWorkTaskId(file, fallbackTaskId) {
  const id = file?.taskId || file?.parameters?.workTaskId || fallbackTaskId;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

function uniqueZipEntryName(name, used) {
  const raw = String(name || 'file').split(/[/\\]/).pop() || 'file';
  const key = raw.toLowerCase();
  if (!used.has(key)) {
    used.add(key);
    return raw;
  }
  const dot = raw.lastIndexOf('.');
  const ext = dot > 0 ? raw.slice(dot) : '';
  const base = dot > 0 ? raw.slice(0, dot) : raw;
  let n = 2;
  let candidate = `${base}-${n}${ext}`;
  while (used.has(candidate.toLowerCase())) {
    n += 1;
    candidate = `${base}-${n}${ext}`;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

export function zipEntryNameForOutput(file, used = new Set()) {
  return uniqueZipEntryName(getFileDisplayName(file), used);
}

function fileKey(file) {
  return file?.id || file?.cdnPath || file?.url || file?.name || null;
}

function fileDedupeKeys(file) {
  if (!file) return [];
  const keys = [];
  const id = fileKey(file);
  if (id) keys.push(`id:${id}`);
  if (file.fingerprint) keys.push(`fp:${file.fingerprint}`);
  const name = String(file.name || file.originalName || file.path || '').split('/').pop().toLowerCase();
  const size = file.sizeInBytes || file.size || 0;
  if (name) keys.push(`name:${name}|${size}`);
  return keys;
}

/**
 * Collect Inputs (user uploads), Outputs (AI-generated files), and Sources
 * (web citations) from conversation messages. Pending composer attachments
 * count as inputs.
 */
export function collectSourcesAndOutputs(messages = [], pendingComposerFiles = [], { task, taskId, conversationId } = {}) {
  const inputs = [];
  const outputs = [];
  const sources = [];
  const seenFiles = new Set();
  const seenUrls = new Set();

  const pushFile = (file, messageId, role) => {
    if (!file) return;
    const key = fileKey(file);
    const aliases = fileDedupeKeys(file);
    if (!key || aliases.some((alias) => seenFiles.has(alias))) return;
    aliases.forEach((alias) => seenFiles.add(alias));

    const entry = {
      kind: 'file',
      id: key,
      file,
      messageId: messageId || file.messageId || null,
    };

    const source = file?.source;
    // A file the user attached this turn is an input even if it was
    // generated in an earlier thread (library "Include generated files").
    if (role === 'user') {
      inputs.push(entry);
      return;
    }
    if (isInferenceFile(file) || (role === 'assistant' && source !== 'upload')) {
      outputs.push(entry);
      return;
    }
    if (source === 'upload' || !role) {
      inputs.push(entry);
    }
  };

  // Work's artifact inventory survives pagination and context compaction.
  // Supplement the visible message page, while retaining its hydrated Files.
  const ownsTask = taskId && String(task?.id || task?._id || '') === String(taskId)
    && (!task?.conversationId || String(task.conversationId) === String(conversationId));
  const taskMessages = ownsTask && Array.isArray(task.artifacts) ? [{
    role: 'assistant',
    files: task.artifacts.map(artifact => ({
      ...artifact,
      ...artifact.file,
      id: artifact.file?.id || artifact.file?.fileId || artifact.fileId || artifact.id || artifact.path || artifact.name,
      name: artifact.file?.name || artifact.name || String(artifact.path || '').split('/').pop(),
      taskId,
      conversationId,
      source: 'inference',
    })),
  }] : [];

  [...(Array.isArray(messages) ? messages : []), ...taskMessages].forEach((m) => {
    const messageId = m?.id || m?._id || null;
    const role = m?.role;

    if (Array.isArray(m?.files)) {
      m.files.forEach((f) => pushFile(f, messageId, role));
    }
    if (Array.isArray(m?.images)) {
      m.images.forEach((img) => {
        if (!img) return;
        pushFile(
          typeof img === 'string'
            ? {
              id: img,
              cdnPath: img,
              url: img,
              fileType: 'image',
              source: role === 'assistant' ? 'inference' : 'upload',
            }
            : img,
          messageId,
          role
        );
      });
    }
    if (Array.isArray(m?.videos)) {
      m.videos.forEach((v) => {
        if (!v) return;
        pushFile(
          typeof v === 'string'
            ? {
              id: v,
              cdnPath: v,
              url: v,
              fileType: 'video',
              source: role === 'assistant' ? 'inference' : 'upload',
            }
            : v,
          messageId,
          role
        );
      });
    }

    if (Array.isArray(m?.citations)) {
      m.citations.forEach((citation, index) => {
        const url = citation?.url;
        if (!url || seenUrls.has(url)) return;
        const host = citation?.hostname || getBaseHostname(url);
        if (isPlatformCdnHost(host) || isPlatformCdnHost(getBaseHostname(url))) return;
        seenUrls.add(url);
        sources.push({
          kind: 'link',
          id: `${messageId || 'msg'}-cite-${index}-${url}`,
          citation,
          messageId,
          url,
          title: citation?.title || citation?.hostname || getBaseHostname(url),
          hostname: citation?.hostname || getBaseHostname(url),
        });
      });
    }

    if (Array.isArray(m?.parameters?.coworkerArtifacts)) {
      presentWorkArtifacts(m.parameters.coworkerArtifacts).forEach((artifact) => {
        if (!artifact) return;
        pushFile({
          id: artifact.path || artifact.name,
          name: artifact.name,
          originalName: artifact.name,
          path: artifact.path,
          mimeType: artifact.mimeType,
          fileType: artifact.fileType,
          size: artifact.size,
          sizeInBytes: artifact.size,
          source: 'inference',
        }, messageId, 'assistant');
      });
    }
  });

  (Array.isArray(pendingComposerFiles) ? pendingComposerFiles : []).forEach((file) => {
    if (!file) return;
    const key = fileKey(file);
    const aliases = fileDedupeKeys(file);
    if (!key || aliases.some((alias) => seenFiles.has(alias))) return;
    aliases.forEach((alias) => seenFiles.add(alias));
    inputs.push({
      kind: 'file',
      id: key,
      file,
      messageId: null,
      isPending: true,
    });
  });

  // Book page PNGs, generate_video clips, and gateway UUIDs also land on
  // assistant `files` / `images` — not only coworkerArtifacts. Collapse the
  // finished Outputs rail the same way as the in-thread cards.
  const keptFiles = new Set(presentWorkArtifacts(outputs.map((entry) => entry.file)));
  const visibleOutputs = outputs.filter((entry) => keptFiles.has(entry.file));

  return { inputs, outputs: visibleOutputs, sources };
}
